// Media Library upload — the PURE decision layer (ML-UP-01 / ML-UP-02).
//
// THE BUG (measured live 2026-07-28, web-stg bundle main.1d538a78.js, acct 12145;
// RE-MEASURED still-broken on sandbox HEAD 2026-08-08): the /media-library page's
// ONLY call to action — the empty state's "Upload media" button, sitting directly
// under the copy "Everything you upload lives here / Upload once — reuse it on any
// Wheel" — was wired to `onUpload={() => mediaQuery.refetch()}`. Clicking it
// silently re-ran the list query: no file chooser, no dialog, no navigation,
// nothing a user can perceive. A live DOM scan of the whole page measured
// `input[type=file]` count **0** and **zero** controls matching
// /upload|add media|new media/ — so there was no way to put a file INTO the Media
// Library from the Media Library at all, in any state.
// (Positive control, G-146: the same scan found 8 real controls — New Folder,
// the six type tabs, Search media — so the scan works; the absence is real.)
//
// Everything here is pure so the size/type/summary rules are unit-testable without
// a browser, an S3 bucket, or a mocked network — the container owns the I/O.

// The union of the app's existing per-type allowlists
// (SegmentBuilder/Shared/UploadMedia `allowedMimeTypes`). The Media Library is
// type-agnostic — it holds images, video, audio and documents — so it accepts the
// union rather than inventing a new set. Kept as extensions (what the presign
// endpoint's server-side allowlist keys on) plus their MIME types (what the OS
// file chooser filters on).
export const MEDIA_LIBRARY_EXTENSIONS = [
  // images
  "png", "jpg", "jpeg", "gif", "heic", "svg", "webp", "avif",
  // video
  "mp4", "mov", "avi", "mkv",
  // audio
  "mp3", "wav",
  // documents
  "pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "csv",
];

// `accept` for the hidden <input type=file>. Extensions (not bare MIME globs) so
// the chooser's filter matches the server-side extension allowlist exactly — a
// file the picker offers is a file the presign endpoint will sign.
export const MEDIA_LIBRARY_ACCEPT = MEDIA_LIBRARY_EXTENSIONS.map((e) => `.${e}`).join(",");

// Same per-file ceiling the rest of the app's upload paths enforce
// (UploadMedia images/documents/audio branch). Applied to EVERY file here, so the
// Media Library has no equivalent of the video-branch gap (CM1/C4.1).
export const MAX_UPLOAD_MB = 50;

/**
 * Where a Media Library upload is filed.
 *
 * It belongs to NO wheel, so it must not go through `getWheelUploadPath`, whose
 * wheel-less fallback is `uploads/<uid>/wheels/unsaved/` — that would pollute the
 * unsaved-wheel namespace and read as a bug in moderation/quarantine logs (the
 * very failure that fallback exists to avoid). It gets its own honest prefix.
 *
 * Safe because the library lists arbitrary-depth keys under `uploads/<user_id>/`
 * — measured live 2026-07-28: a listed item's path is
 * `uploads/12145/wheels/3845/activities/19327/nodes.txt`, so the listing walks the
 * whole user prefix rather than a fixed shallow one.
 *
 * Lives here (pure, dependency-free) rather than in utils/fileUpload.js so it is
 * unit-testable without pulling that module's heic2any/S3 imports into jsdom.
 */
export const getMediaLibraryUploadPath = ({ user_id } = {}) => {
  const uid = user_id != null && user_id !== "" ? user_id : "unknown";
  return `uploads/${uid}/library/`;
};

export const fileExtension = (name) => {
  const parts = String(name || "").split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "";
};

export const isAllowedFile = (file) =>
  MEDIA_LIBRARY_EXTENSIONS.includes(fileExtension(file && file.name));

export const isTooLarge = (file, maxMb = MAX_UPLOAD_MB) =>
  Number(file && file.size) / 1000000 > maxMb;

/**
 * Split a chooser selection into what we will upload and what we will refuse,
 * WITHOUT throwing away the reasons — the caller needs them to tell the user
 * which files were dropped and why (an upload that silently skips files is the
 * same class of bug as the dead CTA this module exists to fix).
 */
export const partitionUploadFiles = (files, { maxMb = MAX_UPLOAD_MB } = {}) => {
  const list = Array.from(files || []);
  const accepted = [];
  const tooLarge = [];
  const wrongType = [];
  for (const file of list) {
    if (!isAllowedFile(file)) wrongType.push(file);
    else if (isTooLarge(file, maxMb)) tooLarge.push(file);
    else accepted.push(file);
  }
  return { accepted, tooLarge, wrongType };
};

const names = (files) => files.map((f) => f.name).join(", ");

/** Plain-language refusal copy, or null when nothing was refused. */
export const rejectionMessage = ({ tooLarge = [], wrongType = [] } = {}, maxMb = MAX_UPLOAD_MB) => {
  const parts = [];
  if (wrongType.length) {
    parts.push(
      `${names(wrongType)} ${wrongType.length === 1 ? "isn't" : "aren't"} a file type the Media Library accepts.`
    );
  }
  if (tooLarge.length) {
    parts.push(`${names(tooLarge)} is over the ${maxMb}MB limit.`);
  }
  return parts.length ? parts.join(" ") : null;
};

/**
 * Upload every accepted file in sequence and tally the outcome.
 *
 * Pure w.r.t. the transport: `upload` is injected, so the sequencing, the
 * keep-going-after-a-failure rule and the tally are unit-testable without S3.
 * Sequential (not Promise.all) on purpose — a parallel burst of presign +
 * direct-to-S3 PUTs from one browser is exactly how an upload of many files
 * starts failing halfway with no useful error.
 */
export const uploadAll = async (files, upload) => {
  let ok = 0;
  let failed = 0;
  for (const file of files) {
    try {
      await upload(file);
      ok += 1;
    } catch (err) {
      failed += 1;
      console.error("Media Library upload failed:", err);
    }
  }
  return { ok, failed };
};

/** Result copy after the uploads settle. `null` when there is nothing to say. */
export const uploadSummaryMessage = ({ ok = 0, failed = 0 } = {}) => {
  if (ok && !failed) return `${ok} file${ok === 1 ? "" : "s"} added to your Media Library.`;
  if (ok && failed) return `${ok} of ${ok + failed} files uploaded — ${failed} failed. Please try again.`;
  if (!ok && failed) return `Upload failed. Please try again.`;
  return null;
};
